/**
 * Dose Dash: a day passes, slots come due, you catch each one before the sun
 * leaves it.
 *
 * What it exercises is prospective memory — remembering to do a thing at a
 * future moment — which is the skill that actually predicts whether someone
 * takes a medicine on time. The link to FulLife's real scheduling is the
 * point of the game, but the copy stays on the game: generic tablets, no
 * dosing, nothing that reads as instruction. Nothing here claims a health
 * benefit either (see kente-rules for why that line matters).
 */

export interface DoseSlot {
  key: string;
  /** What the player taps — a time of day, not a medicine. */
  label: string;
  /** Where in the day it falls, 0 = sunrise, 1 = sunset. */
  at: number;
}

/** Three slots, matching the morning/afternoon/evening shape of a real
 *  schedule. Deliberately uneven spacing: an evenly spaced day is a metronome
 *  and stops being a memory test. */
export const DOSE_SLOTS: DoseSlot[] = [
  { key: "morning", label: "Morning", at: 0.18 },
  { key: "afternoon", label: "Afternoon", at: 0.52 },
  { key: "evening", label: "Evening", at: 0.83 },
];

/** How long a whole day takes in the spike. The game's own day length comes
 *  from the curve in dose-rules and starts here. */
export const DAY_MS = 15_000;

/** How wide a slot's window is, as a fraction of the day. Miss it and it is
 *  gone — that is the whole mechanic. */
export const SLOT_WINDOW = 0.12;

export type SlotState = "pending" | "open" | "taken" | "missed" | "wrong";

/**
 * What sits on the tray when a slot opens: the tablet the day asked for, a
 * lookalike tablet, and everyday things that are simply not what was asked
 * for.
 *
 * The distractors are the game. Timing alone is a reflex test; having to NOT
 * grab the nearest thing is inhibition, which is the half of prospective
 * memory that actually fails in real life.
 *
 * Note the framing, which matters on a health brand: nothing here is a bad
 * choice. Water and bread are not mistakes, they are just not what this slot
 * asked for. No item is scolded and no food is judged.
 */
export interface DoseItem {
  key: string;
  label: string;
  /** Tablets are the only takeable kind; the rest are there to be resisted. */
  kind: "tablet" | "other";
  colour: string;
}

/** Which tablet is "yours" changes each day, so the right answer cannot
 *  become muscle memory — you have to hold today's tablet in mind while the
 *  day runs. */
export const TABLETS: DoseItem[] = [
  { key: "tablet-blue", label: "Blue tablet", kind: "tablet", colour: "#4C8DD9" },
  { key: "tablet-white", label: "White tablet", kind: "tablet", colour: "#E8E8E8" },
  { key: "tablet-pink", label: "Pink tablet", kind: "tablet", colour: "#E8809B" },
];

/**
 * One naming register across the whole tray (Ken, 2026-09-04). Proper dish
 * names beside "Blue tablet" read as two different games — and since the
 * target has to be a tablet, and tablets can only be told apart by colour,
 * the register everything can share is plain everyday nouns.
 *
 * So: Water and Tea rather than Jollof and Kelewele. The Ghanaian dishes get
 * their own game — Makola Market Plate is entirely about them, where the
 * names are the point rather than a mismatch.
 */
export const DISTRACTORS: DoseItem[] = [
  { key: "water", label: "Water", kind: "other", colour: "#7FC8E8" },
  { key: "tea", label: "Tea", kind: "other", colour: "#B5773C" },
  { key: "bread", label: "Bread", kind: "other", colour: "#C98A4B" },
  { key: "banana", label: "Banana", kind: "other", colour: "#E3C24B" },
];

/** Today's tablet, plus a lookalike and two everyday items — shuffled, so
 *  position is never the answer either. */
export function buildTray(
  todaysTablet: DoseItem,
  random: () => number = Math.random,
): DoseItem[] {
  const decoy = pickOne(
    TABLETS.filter((tablet) => tablet.key !== todaysTablet.key),
    random,
  );
  const others = shuffle(DISTRACTORS, random).slice(0, 2);
  return shuffle([todaysTablet, decoy, ...others], random);
}

export function pickDayTablet(random: () => number = Math.random): DoseItem {
  return pickOne(TABLETS, random);
}

function pickOne<T>(items: T[], random: () => number): T {
  return items[Math.floor(random() * items.length)];
}

function shuffle<T>(items: T[], random: () => number): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/**
 * Sky colours through the day, as four stops from zenith to horizon rather
 * than two. Two stops give a flat wash; four give the band of light that sits
 * on the horizon at dawn and dusk, which is most of what makes a sky read as
 * a sky. Interpolated per frame, so the day is continuous rather than three
 * backdrops.
 */
export const SKY_STOPS = {
  dawn: ["#101A33", "#2E3A63", "#B4607A", "#F2A15C"],
  noon: ["#1E6FA8", "#3F97CE", "#9FD3EE", "#DCEEF7"],
  dusk: ["#150F26", "#3B2350", "#B44C4C", "#F0813C"],
};

/** The sun's own colour, which is not the sky's: low sun reads orange, high
 *  sun reads near-white. */
export const SUN_COLOURS = {
  dawn: "#FF9A47",
  noon: "#FFF6D2",
  dusk: "#FF6F3C",
};

/** Land, back to front. Layers of silhouette at different darknesses is what
 *  gives a flat scene depth without a single light. */
export const LAND = {
  farHill: "#1B2436",
  nearHill: "#111726",
  ground: "#0C1019",
  palm: "#0A0E16",
};

export const SLOT_COLOURS: Record<SlotState, string> = {
  pending: "#3B3B3B",
  open: "#FFCC08",
  taken: "#1B7A3E",
  // Missed (the sun passed) and wrong (grabbed the wrong thing) are different
  // failures and worth telling apart when tuning.
  missed: "#8A2A2A",
  wrong: "#C8102E",
};

/** Where in the day we are, from the wall clock. Both the scene and the page
 *  derive from this: the scene to move the sun smoothly, the page to decide
 *  which slot is open. */
export function dayProgress(
  startedAtMs: number,
  now: number,
  dayMs: number = DAY_MS,
): number {
  return Math.min(1, Math.max(0, (now - startedAtMs) / dayMs));
}

export function isSlotOpen(
  slot: DoseSlot,
  progress: number,
  window: number = SLOT_WINDOW,
): boolean {
  return Math.abs(progress - slot.at) <= window / 2;
}

/** A slot the sun has already passed without it being taken. */
export function hasSlotPassed(
  slot: DoseSlot,
  progress: number,
  window: number = SLOT_WINDOW,
): boolean {
  return progress > slot.at + window / 2;
}
